import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Business as BusinessIcon,
  Domain as DomainIcon,
  Storage as StorageIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSystem } from '../context/SystemContext';

const TenantDetails = () => {
  const { tenantId } = useParams();
  const { tenants } = useSystem();
  const [tenant, setTenant] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const foundTenant = tenants.find(t => t.id === tenantId);
    if (foundTenant) {
      setTenant(foundTenant);
      setFormData(foundTenant);
    }
  }, [tenantId, tenants]);

  const handleSave = () => {
    // TODO: Implement save functionality
    console.log('Save tenant:', formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData(tenant);
    setIsEditing(false);
  };

  if (!tenant) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Kiracı Bulunamadı
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Kiracı Detayları - {tenant.name}
        </Typography>
        <Box>
          {!isEditing ? (
            <Button variant="contained" onClick={() => setIsEditing(true)}>
              Düzenle
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={handleCancel}>
                İptal
              </Button>
              <Button variant="contained" onClick={handleSave}>
                Kaydet
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BusinessIcon />
                <Typography variant="h6">Temel Bilgiler</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Firma Adı"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Domain"
                    value={formData.domain || ''}
                    onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="E-posta"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    disabled={!isEditing}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* System Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SettingsIcon />
                <Typography variant="h6">Sistem Ayarları</Typography>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Durum</InputLabel>
                    <Select
                      value={formData.status || 'active'}
                      label="Durum"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      disabled={!isEditing}
                    >
                      <MenuItem value="active">Aktif</MenuItem>
                      <MenuItem value="inactive">Pasif</MenuItem>
                      <MenuItem value="suspended">Askıya Alınmış</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features?.b2b || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, b2b: e.target.checked }
                        })}
                        disabled={!isEditing}
                      />
                    }
                    label="B2B Portal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features?.b2c || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, b2c: e.target.checked }
                        })}
                        disabled={!isEditing}
                      />
                    }
                    label="B2C Portal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.features?.production || false}
                        onChange={(e) => setFormData({
                          ...formData,
                          features: { ...formData.features, production: e.target.checked }
                        })}
                        disabled={!isEditing}
                      />
                    }
                    label="Üretim Modülü"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                İstatistikler
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {tenant.statistics?.users || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Kullanıcı
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {tenant.statistics?.products || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ürün
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {tenant.statistics?.orders || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Sipariş
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" color="primary">
                      {tenant.statistics?.revenue || 0}₺
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gelir
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Son Aktiviteler
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tarih</TableCell>
                      <TableCell>Kullanıcı</TableCell>
                      <TableCell>İşlem</TableCell>
                      <TableCell>Detay</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenant.activities?.map((activity, index) => (
                      <TableRow key={index}>
                        <TableCell>{new Date(activity.timestamp).toLocaleString()}</TableCell>
                        <TableCell>{activity.user}</TableCell>
                        <TableCell>{activity.action}</TableCell>
                        <TableCell>{activity.details}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TenantDetails;
